/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useToastManager } from "@norican/react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type AcceptMatchedResult,
  approveLearnedAlias,
  type BulkResult,
  captureFields,
  type CaptureFieldsRequest,
  type CaseActivityRequest,
  classifyIntent,
  type CorrectionRequest,
  correctLine,
  type CrmMachinesView,
  type CustomerResolveRequest,
  getAutonomyMetrics,
  getCase,
  getEngineStatus,
  getEntityRuleTable,
  getExecutiveMetrics,
  getHealth,
  getLearningStats,
  getMe,
  getMyAttention,
  getRuleSet,
  getWorkloadMetrics,
  type IntentClassifyRequest,
  type LineOverrideRequest,
  listLearnedProposals,
  logout,
  previewQuote,
  type QueueBacklog,
  type QuotePreviewRequest,
  resolveCustomer,
  resolveLine,
  type ResolveRequest,
  retractLearnedAlias,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

import { createAnswerDraft, getAnswerDraft } from "./answer-draft";
import { type ApiError, client } from "./client";
import { customerMemoryKey, getCustomerMemory } from "./customer-memory";
import {
  addDiscussionCommentRaw,
  createDiscussionRaw,
  decideDiscussionRaw,
  type DecisionFrame,
  type DiscussionCreateRequest,
  type DiscussionDecisionRequest,
  discussionKeys,
  type DiscussionKind,
  getDiscussionRaw,
  listDiscussionsRaw,
  setDecisionFrameRaw,
} from "./discussions";
import { toExecutive } from "./executive";
import { feedbackKey, listCaseFeedback, postCaseFeedback } from "./feedback";
import {
  addFeedbackComment,
  createFeedback,
  type FeedbackCommentRequest,
  type FeedbackCreateRequest,
  type FeedbackFilters,
  feedbackHubKeys,
  type FeedbackTriageRequest,
  getFeedback,
  getFeedbackSummary,
  getFeedbackUnreadCount,
  listFeedback,
  listMyFeedback,
  markFeedbackSeen,
  type MyFeedbackFilters,
  triageFeedback,
} from "./feedback-hub";
import { getInstalledBase, installedBaseKey } from "./installed-base";
import type {
  AreaInfo,
  CaseAssignRequest,
  CaseCustomerRequest,
  CaseEvent,
  CaseFeedbackRequest,
  CaseMail,
  CaseStatusRequest,
  CaseWorkResult,
  CatalogueItem,
  CustomerHit,
  DataHealth,
  FoDraftPreview,
  LineAddRequest,
  LineDecisionRequest,
  LinePriceApproval,
  LiveQuoteCaseDetail,
  LiveQuoteCaseSummary,
  LiveUserProfile,
  OpsMetrics,
  QueueCounts,
  QueueFilters,
  QueuePage,
} from "./live-types";
import {
  getCustomerMatchingMetrics,
  getMarginThresholds,
  getPilotMetrics,
  matchingDays,
  type PilotWindow,
} from "./pilot";
import {
  approvalKey,
  approvalMutationOptions,
  getQuotationApproval,
  postLinePriceApproval,
  postQuotationApproval,
  postQuotationWithdraw,
} from "./quotation-approval";
import {
  applyAcceptResult,
  type BulkRequest,
  type BulkRow,
  bulkSender,
  postAcceptMatched,
  readCaseWork,
  runBulk,
  sendCaseActivity,
  sendWithRowVersion,
} from "./sales-actions";
import { loadCaseTranslation, type TranslationTarget } from "./translation";
import {
  type CachedCase,
  caseWriteKey,
  expectedVersion,
  type LineQuantitiesAnswer,
  LOOK_AGAIN_PENDING_MESSAGE,
  optimisticDecision,
  optimisticPrice,
  optimisticQuantities,
  optimisticQuantity,
  postAddedLineRemove,
  postCaseAssign,
  postCaseCustomer,
  postCaseStatus,
  postLineAdd,
  postLineDecision,
  postLineOverride,
  postLineQuantities,
  postLookAgain,
  postRejectProposal,
  type WorkAnswer,
  workFailure,
  type WorkMutationContext,
  workMutationOptions,
  type WorkTarget,
} from "./work-actions";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      // `areas` is on the wire but not in the generated type yet (see live-types).
      return data as LiveUserProfile | undefined;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── RFQ engine — configuration probe ─────────────────────────────────────

/**
 * Whether the RFQ engine's runtime sources are configured.
 *
 * `GET /api/status` is designed to always answer 200 (it reports configuration
 * rather than requiring it), so this is a normal cached query — no `retry:false`
 * needed. It is the anchor for the "engine not configured — showing sample data"
 * banner and for deciding whether the live pipeline hooks below will 503.
 */
export function useEngineStatus() {
  return useQuery({
    queryKey: ["engine-status"],
    queryFn: async () => {
      const { data } = await getEngineStatus();
      return data;
    },
  });
}

// ─── RFQ case store — live queue + case reads ─────────────────────────────
//
// Page-load READS off the local case store (`GET /api/queue`,
// `GET /api/cases/{id}`), so they are queries, not mutations. Both are read-only
// against every external system — the case store is our own local app DB.
//
// The store is optional: until it is populated the endpoints answer 501 (route
// reserved) or 503 (store unconfigured), and a bad id answers 422. `retry: false`
// keeps an unpopulated store from turning any of those into a retry-storm — the
// `data.ts` seam catches the throw and falls back to the mock fixtures, so the
// pages never go blank. The token-agnostic ingest that fills the store is a
// separate local-only concern; these hooks just read whatever it has persisted.

/** How often the queue re-reads the store while the page is open (the ingest job runs every 5 min). */
const QUEUE_REFETCH_MS = 60_000;

/** How often an open case is read again while a "Look again" request waits for I2Q. */
export const LOOK_AGAIN_POLL_MS = 15_000;

/** How often an open case is read again while I2Q reads the mail (again). */
export const CASE_READING_POLL_MS = 5_000;

/**
 * How often the open case is read again on its own, or `false` for never. While I2Q reads
 * the mail (state `queued` or `processing`, for example after a customer confirmation)
 * the page asks every 5 s, so the result arrives without a reload, a release or a take
 * (ease round, 13 Oct 2026: "Reading again" had no end on the open page). While a "Look
 * again" request waits it asks every 15 s. Otherwise it does not ask.
 */
export function casePollMs(
  detail:
    | { state?: string | null; investigation?: { pending?: boolean | null } | null }
    | null
    | undefined,
): number | false {
  if (detail?.state === "queued" || detail?.state === "processing") {
    return CASE_READING_POLL_MS;
  }
  return detail?.investigation?.pending === true ? LOOK_AGAIN_POLL_MS : false;
}

/**
 * One page of the queue as the pages consume it. `counts` is null for a legacy array
 * answer; `backlog` is null when the backend does not send it.
 */
export type QueuePageResult = Omit<QueuePage, "counts" | "backlog"> & {
  counts: QueueCounts | null;
  backlog: QueueBacklog | null;
};

/** Drop empty filters so equal views share one cache entry and the URL stays short. */
function queueQuery(filters: QueueFilters): Record<string, unknown> {
  const query: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    query[key] = value;
  }
  return query;
}

/**
 * Read one page of the queue (`GET /api/queue`) outside a hook, e.g. for "Next case"
 * when the order the queue left behind runs out. Same request as {@link useQueue}.
 */
export async function fetchQueuePage(filters: QueueFilters): Promise<QueuePageResult> {
  const { data } = await client.get<{ 200: QueuePage | LiveQuoteCaseSummary[] }, unknown, true>({
    url: "/queue",
    query: queueQuery(filters),
    throwOnError: true,
  });
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      limit: data.length,
      offset: 0,
      counts: null,
      backlog: null,
    };
  }
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: data?.total ?? 0,
    limit: data?.limit ?? filters.limit ?? 50,
    offset: data?.offset ?? filters.offset ?? 0,
    counts: data?.counts ?? null,
    backlog: data?.backlog ?? null,
  };
}

/**
 * One page of the case queue from the local store (`GET /api/queue`).
 *
 * Server-driven: the view, the filters, the search and the paging are query
 * parameters (arrays go as repeated params), and the answer carries the page, the
 * total and the per-view counts. The previous page stays on screen while the next
 * one loads, and the page re-reads every 60 s so newly ingested cases appear without
 * a reload. A legacy array answer (older backend) is wrapped as a single page.
 */
export function useQueue(filters: QueueFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ["queue", filters],
    queryFn: () => fetchQueuePage(filters),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
    refetchInterval: QUEUE_REFETCH_MS,
  });
}

/**
 * One stored case by id (`GET /api/cases/{id}`). Disabled for an empty id.
 *
 * Typed as {@link LiveQuoteCaseDetail}: the contract adds an optional `outcome`
 * (the rep's actual reply vs the draft) that the regenerated SDK will carry.
 */
export function useCase(caseId: string) {
  return useQuery({
    queryKey: ["case", caseId],
    queryFn: async () => {
      const { data } = await getCase({ path: { case_id: caseId } });
      return data as LiveQuoteCaseDetail | undefined;
    },
    enabled: caseId.trim().length > 0,
    retry: false,
    // While I2Q reads the mail, or a "Look again" request waits, the case is read again
    // on its own, so the result arrives on the open page. No polling otherwise.
    refetchInterval: (query) => casePollMs(query.state.data),
  });
}

// ─── Live-data endpoints (hand-written until the SDK is regenerated) ──────
//
// `GET /queue` (page object), `GET /cases/by-message`, `GET /cases/{id}/mail`,
// `GET /status/data` and `GET /metrics/ops` are ahead of the generated SDK; until it
// is regenerated, these hooks call the configured @hey-api client directly (same base URL, interceptors and `ApiError`
// shape as every generated call) with the hand-written types in `live-types.ts`.
// TODO(sdk-regen): swap `client.get` for the generated `getCaseByMessage` /
// `getOpsMetrics` functions and drop `live-types.ts`.

/**
 * The stored case for an Outlook message (`GET /api/cases/by-message`).
 *
 * Keyed by the RFC 5322 `internetMessageId` the add-in reads from the open item.
 * A 404 is a normal answer ("this mail was never ingested") and resolves to
 * `null`, so the add-in falls back to its live preview; any other failure is an
 * error. Disabled for an empty id. Read-only over the local case store.
 */
export function useCaseByMessage(messageId: string | null | undefined) {
  const id = messageId?.trim() ?? "";
  return useQuery({
    queryKey: ["case-by-message", id],
    queryFn: async (): Promise<LiveQuoteCaseDetail | null> => {
      try {
        const { data } = await client.get<{ 200: LiveQuoteCaseDetail }, unknown, true>({
          url: "/cases/by-message",
          query: { message_id: id },
          throwOnError: true,
        });
        return data ?? null;
      } catch (error) {
        if ((error as ApiError | null)?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: id.length > 0,
    retry: false,
  });
}

/**
 * The original mail of a case as plain text (`GET /api/cases/{id}/mail`).
 *
 * Read from our own case store, never from the mailbox. `available` is false while
 * the mail is still waiting for the engine. Disabled for an empty id.
 */
export function useCaseMail(caseId: string) {
  return useQuery({
    queryKey: ["case-mail", caseId],
    queryFn: async () => {
      const { data } = await client.get<{ 200: CaseMail }, unknown, true>({
        url: "/cases/{case_id}/mail",
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    enabled: caseId.trim().length > 0,
    retry: false,
    // A removed original asked for again comes back with the next ingest run.
    refetchInterval: (query) =>
      query.state.data?.restore === "requested" ? MAIL_RESTORE_POLL_MS : false,
  });
}

/** How often a mail whose original is being fetched again is read again. */
const MAIL_RESTORE_POLL_MS = 30_000;

/**
 * Ask I2Q to fetch a removed original mail again (`POST /api/cases/{id}/mail/restore`).
 * Only marks the case in the app DB; the ingest job reads the mail back from the mailbox.
 */
export function useRequestMailRestore(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await client.post<{ 202: CaseMail }, unknown, true>({
        url: "/cases/{case_id}/mail/restore",
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      logger.debug("[case] original mail requested again", { caseId });
      queryClient.setQueryData(["case-mail", caseId], data);
    },
  });
}

/**
 * Whether the live data is current (`GET /api/status/data`).
 *
 * Never answers 503: an unreachable store comes back as health "stale" with one
 * issue. Re-read every 60 s; drives the health banner and the queue header.
 */
export function useDataHealth() {
  return useQuery({
    queryKey: ["data-health"],
    queryFn: async () => {
      const { data } = await client.get<{ 200: DataHealth }, unknown, true>({
        url: "/status/data",
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    refetchInterval: QUEUE_REFETCH_MS,
  });
}

/**
 * The areas of the caller's scope (`GET /api/areas`, stage 3): label, mailboxes, whether
 * the item catalogue is live and whether sent documents are read for it.
 *
 * An older backend without the route answers 404: that resolves to an empty list, so
 * every label falls back to the upper-case code and nothing is retried.
 */
export function useAreas() {
  return useQuery({
    queryKey: ["areas"],
    queryFn: async (): Promise<AreaInfo[]> => {
      try {
        const { data } = await client.get<{ 200: AreaInfo[] }, unknown, true>({
          url: "/areas",
          throwOnError: true,
        });
        return Array.isArray(data) ? data : [];
      } catch (error) {
        if ((error as ApiError | null)?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * What a quotation in the order system would contain (`GET /api/cases/{id}/fo-draft`,
 * stage 3). A preview: the route is GET only and nothing is created anywhere.
 *
 * Disabled until the rep asks for it ("Prepare quotation"), and never cached as fresh,
 * so asking again reads the case as it is now.
 */
export function useFoDraft(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["case-fo-draft", caseId],
    queryFn: async () => {
      const { data } = await client.get<{ 200: FoDraftPreview }, unknown, true>({
        url: "/cases/{case_id}/fo-draft",
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
    staleTime: 0,
  });
}

/**
 * Live ops metrics for the dashboard (`GET /api/metrics/ops?days=N`).
 *
 * Read-only aggregate over the local app DB: mail/RFQ volume, state split, per-area
 * counts, rep-reply agreement, ingest run status and master-data freshness, plus
 * the egress-gate state. `retry: false` so an unconfigured store (503) shows the
 * error state at once instead of a retry storm.
 */
export function useOpsMetrics(window: number | OpsWindow = 30, enabled = true) {
  const query: Record<string, string | number> =
    typeof window === "number"
      ? { days: window }
      : { weeks: window.weeks, ...(window.end ? { end: window.end } : {}) };
  return useQuery({
    // A day window keeps its old key (the queue reads it); a week window is its own key.
    queryKey:
      typeof window === "number"
        ? ["metrics-ops", window]
        : ["metrics-ops", "weeks", window.weeks, window.end ?? ""],
    queryFn: async () => {
      const { data } = await client.get<{ 200: OpsMetrics }, unknown, true>({
        url: "/metrics/ops",
        query,
        throwOnError: true,
      });
      return data;
    },
    enabled,
    retry: false,
    refetchInterval: QUEUE_REFETCH_MS,
    // Switching the period keeps the old figures on screen (dimmed) until the new ones land.
    placeholderData: keepPreviousData,
  });
}

/** Completed calendar weeks (Monday to Sunday, UTC) for `GET /metrics/ops?weeks=`. */
export interface OpsWindow {
  weeks: number;
  /** A day of the last week counted; default the last completed week. */
  end?: string;
}

/** What the executive summary or the monthly report is asked for. */
export interface ExecutiveWindow {
  /** Completed weeks in the period (1 to 13). */
  periodWeeks?: number;
  /** Trend weeks (4 to 26). */
  weeks?: number;
  /** A day of the period's last week; default the last completed week. */
  end?: string;
  /** Report mode, `YYYY-MM`. */
  month?: string;
  /** Narrow to these areas (never wider than the caller's). */
  areas?: readonly string[];
}

/** The query key of {@link useExecutiveMetrics}, for tests and prefetching. */
export function executiveKey(window: ExecutiveWindow): readonly unknown[] {
  return [
    "metrics-executive",
    window.month ?? "",
    window.periodWeeks ?? 4,
    window.weeks ?? 10,
    window.end ?? "",
    [...(window.areas ?? [])].sort().join(","),
  ];
}

/**
 * The executive summary and the monthly report (`GET /metrics/executive`). Managers only;
 * the server keeps the scope (403 for an area the caller lacks). Served from a cache on
 * the server, so `staleTime` is generous; switching the period keeps the old figures.
 */
export function useExecutiveMetrics(window: ExecutiveWindow, enabled: boolean) {
  return useQuery({
    queryKey: executiveKey(window),
    queryFn: async () => {
      const { data } = await getExecutiveMetrics({
        query: {
          period_weeks: window.periodWeeks ?? 4,
          weeks: window.weeks ?? 10,
          ...(window.end ? { end: window.end } : {}),
          ...(window.month ? { month: window.month } : {}),
          ...(window.areas && window.areas.length > 0 ? { area: [...window.areas] } : {}),
        },
        throwOnError: true,
      });
      return data ? toExecutive(data) : null;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── RFQ engine — read-only pipeline mutations ────────────────────────────
//
// These wrap the working P2 endpoints. Each returns real engine output when the
// engine is configured and throws a clean `ApiError` with `status === 503` when
// it is not (the caller renders the "not configured" state). They are modelled
// as mutations because each is an explicit, parameterised POST fired on demand,
// not a page-load read — so they never run (or retry) until invoked. Mutations
// already default to `retry: false` (see `query-client.ts`), so an unconfigured
// engine cannot trigger a 503 retry-storm. All are read-only: they resolve /
// classify / preview, and never write to Go Autonomous, the mailboxes or F&O.

/** Resolve one enquiry line to a catalogue item (`POST /api/resolve`). */
export function useResolveLine() {
  return useMutation({
    mutationFn: async (body: ResolveRequest) => {
      const { data } = await resolveLine({ body });
      return data;
    },
  });
}

/** Classify one mail into a business intent (`POST /api/intent/classify`). */
export function useClassifyIntent() {
  return useMutation({
    mutationFn: async (body: IntentClassifyRequest) => {
      const { data } = await classifyIntent({ body });
      return data;
    },
  });
}

/** Identify the customer behind an enquiry (`POST /api/customer/resolve`). */
export function useResolveCustomer() {
  return useMutation({
    mutationFn: async (body: CustomerResolveRequest) => {
      const { data } = await resolveCustomer({ body });
      return data;
    },
  });
}

/** Run the full read-only pipeline and return the glass-box draft (`POST /api/quote/preview`). Dry-run only — never sends or writes. */
export function usePreviewQuote() {
  return useMutation({
    mutationFn: async (body: QuotePreviewRequest) => {
      const { data } = await previewQuote({ body });
      return data;
    },
  });
}

/** Extract logistics/header fields from a raw MIME mail (`POST /api/fields/capture`). */
export function useCaptureFields() {
  return useMutation({
    mutationFn: async (body: CaptureFieldsRequest) => {
      const { data } = await captureFields({ body });
      return data;
    },
  });
}

// ─── Correction-learning flywheel ─────────────────────────────────────────
//
// A reviewer's correction of one resolved line seeds the flywheel: it is recorded
// and (for an `item` verdict) generalised into learned aliases, so the same error
// never happens twice. All three writes below target ONLY the local app DB — never
// Go Autonomous, the mailboxes or F&O. `useCorrectLine` invalidates the case + queue
// (the corrected line's state may change on re-read) and the learned-proposals queue
// (an `item` verdict adds a proposed broad alias). Approve/retract invalidate the
// proposals queue so the review surface reflects the new status immediately.

/** Stable key for the learned-proposals review queue (`GET /api/learned/proposals`). */
const LEARNED_PROPOSALS_KEY = ["learned-proposals"] as const;

/** Arguments for {@link useCorrectLine}: the line's location plus the correction body. */
export interface CorrectLineArgs {
  /** Case id the corrected line belongs to. */
  caseId: string;
  /** Zero-based index of the line within the case. */
  lineIndex: number;
  /** The reviewer's verdict (item / abstain / confirm) and optional context. */
  body: CorrectionRequest;
}

/** Record a reviewer's correction of one resolved line (`POST /api/cases/{id}/lines/{i}/correct`). */
export function useCorrectLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, lineIndex, body }: CorrectLineArgs) => {
      const { data } = await correctLine({
        path: { case_id: caseId, line_index: lineIndex },
        body,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      // The corrected line (and its queue row) may re-read differently, and an
      // `item` verdict adds a proposed broad alias — refresh all three.
      queryClient.invalidateQueries({ queryKey: ["case", variables.caseId] });
      queryClient.invalidateQueries({ queryKey: ["queue"] });
      queryClient.invalidateQueries({ queryKey: LEARNED_PROPOSALS_KEY });
    },
  });
}

/** The proposed broad aliases awaiting review (`GET /api/learned/proposals`). */
export function useLearnedProposals() {
  return useQuery({
    queryKey: LEARNED_PROPOSALS_KEY,
    queryFn: async () => {
      const { data } = await listLearnedProposals();
      return data;
    },
    // The correction store is optional: an unconfigured store answers 503, and the
    // page renders a clear "not configured" state rather than storm-retrying.
    retry: false,
  });
}

/** Approve a proposed broad alias — flip it active (`POST /api/learned/{id}/approve`). */
export function useApproveLearned() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (aliasId: string) => {
      const { data } = await approveLearnedAlias({ path: { alias_id: aliasId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEARNED_PROPOSALS_KEY });
    },
  });
}

/**
 * The learning-effectiveness metric — errors prevented + aliases learned
 * (`GET /api/metrics/learning`).
 *
 * `errors_prevented` is the count of apply-first hits: every time `/resolve` short-circuited
 * the ladder with a learned correction — an error that never repeated. Read-only over the
 * local app DB. The correction store is optional: an unconfigured store answers 503, so this
 * runs with `retry: false` and consumers render a graceful zero/empty state.
 */
export function useLearningStats() {
  return useQuery({
    queryKey: ["learning-stats"],
    queryFn: async () => {
      const { data } = await getLearningStats();
      return data;
    },
    retry: false,
  });
}

/** Retract a learned alias — flip it disabled (`POST /api/learned/{id}/retract`). */
export function useRetractLearned() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (aliasId: string) => {
      const { data } = await retractLearnedAlias({ path: { alias_id: aliasId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEARNED_PROPOSALS_KEY });
    },
  });
}

// ─── Stage 2: working with a case ─────────────────────────────────────────
//
// Four writes (line decision, status, assignee, customer) and three reads (history,
// catalogue search, customer search). The writes reach ONLY the app DB. Each sends
// `expected_version` from the case the page is showing (0 without a work row); the
// answer is applied to the cached case at once, then the case and the queue are
// re-read. A 409 raises the conflict toast and re-reads the case; 422 and 503 are
// left on `mutation.error` for the control to print (`workFailureText`).

/** Characters a catalogue or customer search needs before it runs. */
export const SEARCH_MIN_CHARS = 3;
const SEARCH_LIMIT = 20;

/**
 * Shared plumbing of the case writes (see `workMutationOptions`). Every write also makes
 * the cached quotation preview stale: it is built from the customer, the lines and who
 * has the case, so a take, a release and a customer confirmation change it too (ease
 * round, 13 Oct 2026: the page showed checks of a preview made before the change).
 */
function useWorkMutation<Args, Result extends WorkAnswer = CaseWorkResult>(
  caseId: string,
  target: WorkTarget,
  send: (args: Args, version: number) => Promise<Result | undefined>,
  lineIndexOf?: (args: Args) => number,
  optimistic?: (detail: CachedCase, args: Args) => CachedCase,
) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const options = workMutationOptions<Args, Result>({
    queryClient,
    caseId,
    target,
    send,
    lineIndexOf,
    optimistic,
    notify: (message) =>
      toast.add({ title: "Case changed", description: message, type: "warning" }),
  });
  return useMutation({
    ...options,
    onSuccess: (result: Result | undefined, args: Args) => {
      options.onSuccess(result, args);
      void queryClient.invalidateQueries({ queryKey: ["case-fo-draft", caseId] });
    },
  });
}

/** Arguments of {@link useLineDecision}. `lineIndex` is the line's position in the stored draft. */
export interface LineDecisionArgs extends Omit<LineDecisionRequest, "expected_version"> {
  lineIndex: number;
}

/**
 * Confirm, pick, mark "no item" or undo on one line (`POST /cases/{id}/lines/{i}/decision`).
 * The row shows the decision at once; a failed write puts it back.
 */
export function useLineDecision(caseId: string) {
  return useWorkMutation<LineDecisionArgs>(
    caseId,
    "line",
    ({ lineIndex, ...body }, version) =>
      postLineDecision(caseId, lineIndex, { ...body, expected_version: version }),
    (args) => args.lineIndex,
    (detail, args) => optimisticDecision(detail, args.lineIndex, args.kind, args.item ?? undefined),
  );
}

/**
 * Plumbing of the three line corrections (end-user review F08): the work-write behaviour,
 * with the quotation preview read again, because the corrected lines are priced and checked
 * anew. The case answer itself carries the new prices on the next read.
 */
function useLineCorrection<Args>(
  caseId: string,
  send: (args: Args, version: number) => Promise<CaseWorkResult | undefined>,
  optimistic?: (detail: CachedCase, args: Args) => CachedCase,
) {
  return useWorkMutation<Args>(caseId, "line", send, undefined, optimistic);
}

/** The cached case the moment a quantity, a unit or a price of one line is saved. */
function optimisticOverride(detail: CachedCase, args: LineOverrideArgs): CachedCase {
  if (args.kind !== "edit") {
    return detail;
  }
  let next = detail;
  const qty = typeof args.qty === "number" ? args.qty : null;
  if (qty !== null && Number.isFinite(qty)) {
    next = optimisticQuantity(next, args.lineIndex, qty, args.unit ?? undefined);
  }
  if (typeof args.unit_price === "string" && args.unit_price.trim() !== "" && args.currency) {
    next = optimisticPrice(next, args.lineIndex, args.unit_price, args.currency);
  }
  return next;
}

/**
 * Arguments of {@link useLineOverride}. `lineIndex` is the line's position in the stored
 * draft. The price fields (`unit_price`, `currency`, `price_note`) ride on the same
 * request; a decimal goes as a string ("412.50"). An undo of the price sends
 * `unit_price: null` explicitly and no qty or unit keys.
 */
export interface LineOverrideArgs extends Omit<LineOverrideRequest, "expected_version"> {
  lineIndex: number;
}

/**
 * Reviewed quantity / unit, a price, take a line out, or undo
 * (`POST /cases/{id}/lines/{i}/override`). A quantity and a price show in the cell at once.
 */
export function useLineOverride(caseId: string) {
  return useLineCorrection<LineOverrideArgs>(
    caseId,
    ({ lineIndex, ...body }, version) =>
      postLineOverride(caseId, lineIndex, { ...body, expected_version: version }),
    optimisticOverride,
  );
}

/** Arguments of {@link useLineQuantities}: set one quantity on the lines, or take it off. */
export type LineQuantitiesArgs =
  | { kind: "set"; line_keys: string[]; qty: number; unit?: string }
  | { kind: "restore"; line_keys: string[] };

/**
 * One reviewed quantity on several lines, or the reviewed quantities taken off again
 * (`POST /cases/{id}/lines/quantity`): one write, one version step, one history entry. A
 * set shows in every cell at once.
 */
export function useLineQuantities(caseId: string) {
  return useWorkMutation<LineQuantitiesArgs, LineQuantitiesAnswer>(
    caseId,
    "quantity",
    (args, version) => postLineQuantities(caseId, { ...args, expected_version: version }),
    undefined,
    (detail, args) =>
      args.kind === "set"
        ? optimisticQuantities(detail, args.line_keys, args.qty, args.unit)
        : detail,
  );
}

/** Add a missed line, or change one sales added (`POST /cases/{id}/lines/add`). */
export function useAddLine(caseId: string) {
  return useLineCorrection<Omit<LineAddRequest, "expected_version">>(caseId, (body, version) =>
    postLineAdd(caseId, { ...body, expected_version: version }),
  );
}

/** Delete a line sales added (`POST /cases/{id}/lines/added/remove`). */
export function useRemoveAddedLine(caseId: string) {
  return useLineCorrection<{ line_key: string }>(caseId, (body, version) =>
    postAddedLineRemove(caseId, { ...body, expected_version: version }),
  );
}

/** Mark done, not for us, waiting for customer, or reopen (`POST /cases/{id}/status`). */
export function useCaseStatus(caseId: string) {
  return useWorkMutation<Omit<CaseStatusRequest, "expected_version">>(
    caseId,
    "case",
    (body, version) => postCaseStatus(caseId, { ...body, expected_version: version }),
  );
}

/** Take, take over or release a case (`POST /cases/{id}/assign`). */
export function useCaseAssign(caseId: string) {
  return useWorkMutation<Omit<CaseAssignRequest, "expected_version">>(
    caseId,
    "case",
    (body, version) => postCaseAssign(caseId, { ...body, expected_version: version }),
  );
}

/**
 * Confirm the customer, or clear the choice with a null account (`POST /cases/{id}/customer`).
 * Stage 4: a confirmation teaches the contact memory, so "Why this customer" is read again.
 */
export function useCaseCustomer(caseId: string) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const options = workMutationOptions<Omit<CaseCustomerRequest, "expected_version">>({
    queryClient,
    caseId,
    target: "customer",
    send: (body, version) => postCaseCustomer(caseId, { ...body, expected_version: version }),
    notify: (message) =>
      toast.add({ title: "Case changed", description: message, type: "warning" }),
  });
  return useMutation({
    ...options,
    onSuccess: (result, args) => {
      options.onSuccess(result, args);
      void queryClient.invalidateQueries({ queryKey: customerMemoryKey(caseId) });
      // The preview names the customer and checks who confirmed it.
      void queryClient.invalidateQueries({ queryKey: ["case-fo-draft", caseId] });
    },
  });
}

/**
 * "Look again": ask the deeper search for the case, or for one line (`line_key`), again
 * (`POST /cases/{id}/investigation/look-again`). The request goes through the case queue
 * like a customer confirmation; the case answer then says `investigation.pending` until
 * I2Q has read the case again. A 409 `look_again_pending` (an earlier request still runs)
 * raises its own toast; 422 (not offered, or the line has a decision) stays on
 * `mutation.error` for the control.
 */
export function useLookAgain(caseId: string) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  return useMutation(
    workMutationOptions<{ line_key?: string }>({
      queryClient,
      caseId,
      target: "case",
      send: (body, version) => postLookAgain(caseId, { ...body, expected_version: version }),
      notify: (message) =>
        toast.add({
          title: message === LOOK_AGAIN_PENDING_MESSAGE ? "Looking again" : "Case changed",
          description: message,
          type: "warning",
        }),
    }),
  );
}

/**
 * "Not this one": reject one proposal of the deeper search
 * (`POST /cases/{id}/investigation/reject`). One history entry; nothing is read again by
 * I2Q and no line or customer changes. The case is read again so the proposal shows as
 * rejected. A 404 (I2Q read the case again and the proposal is gone) stays on
 * `mutation.error` for the control, and the case is read again so the page shows the
 * proposals of today.
 */
export function useRejectProposal(caseId: string) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const options = workMutationOptions<{ proposal_id: string }>({
    queryClient,
    caseId,
    target: "case",
    send: (body, version) => postRejectProposal(caseId, { ...body, expected_version: version }),
    notify: (message) =>
      toast.add({ title: "Case changed", description: message, type: "warning" }),
  });
  return useMutation({
    ...options,
    onError: (error: unknown) => {
      options.onError(error);
      if ((error as Partial<ApiError> | null)?.status === 404) {
        logger.debug("[api] the rejected proposal is gone, reading the case again", { caseId });
        void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      }
    },
  });
}

/** Who did what on a case, newest first (`GET /cases/{id}/events`). Read when the block is opened. */
export function useCaseEvents(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["case-events", caseId],
    queryFn: async () => {
      const { data } = await client.get<{ 200: { items: CaseEvent[] } }, unknown, true>({
        url: "/cases/{case_id}/events",
        path: { case_id: caseId },
        throwOnError: true,
      });
      return Array.isArray(data?.items) ? data.items : [];
    },
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

/**
 * Search the item catalogue of the case's area (`GET /cases/{id}/catalogue`).
 *
 * Runs from three characters, 300 ms after the last keystroke. `term` is the text the
 * shown result belongs to.
 */
export function useCatalogueSearch(caseId: string, text: string) {
  const term = useDebouncedValue(text.trim(), 300);
  const query = useQuery({
    queryKey: ["case-catalogue", caseId, term],
    queryFn: async () => {
      const { data } = await client.get<{ 200: { items: CatalogueItem[] } }, unknown, true>({
        url: "/cases/{case_id}/catalogue",
        path: { case_id: caseId },
        query: { q: term, limit: SEARCH_LIMIT },
        throwOnError: true,
      });
      return Array.isArray(data?.items) ? data.items : [];
    },
    enabled: caseId.trim().length > 0 && term.length >= SEARCH_MIN_CHARS,
    retry: false,
  });
  return { ...query, term };
}

// ─── Stage 4: customer memory, feedback, reply draft, approvals, pilot ─────
//
// Every write reaches the app DB only. The reply draft is a text to copy: no route
// sends, replies or creates an Outlook draft. An approval for the order system is an
// intent row; creating quotations in the order system stays switched off.

/** "Why this customer" (`GET /cases/{id}/customer/memory`). Read when the disclosure opens. */
export function useCustomerMemory(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: customerMemoryKey(caseId),
    queryFn: () => getCustomerMemory(caseId),
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

/**
 * "Assemblies matched by this customer's parts" (`GET /cases/{id}/installed-base`). Read when the
 * disclosure opens. Display only.
 */
export function useCaseInstalledBase(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: installedBaseKey(caseId),
    queryFn: () => getInstalledBase(caseId),
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

/**
 * "Machines in CRM" for the case's identified customer (`GET /cases/{id}/crm-machines`).
 *
 * Read-only and display only: the CRM installed base with the F&O BOM link I2Q built.
 * Empty until the CRM sync has built links or when the case has no confirmed customer.
 */
export function useCaseCrmMachines(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["case-crm-machines", caseId],
    queryFn: async () => {
      const { data } = await client.get<{ 200: CrmMachinesView }, unknown, true>({
        url: "/cases/{case_id}/crm-machines",
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The download link of one file inside an attachment: an attached mail or an Office file
 * (`GET /cases/{id}/attachments/{index}/children/{child}`, `getCaseAttachmentChild`).
 *
 * `child` counts from 1, as `source.child` of a line and `child` of an `attachments_read`
 * entry (decision 1 of 14 Oct 2026). The route always answers as a download, like the
 * attachment itself, so a link is enough: no query, nothing cached.
 */
export function caseAttachmentChildHref(caseId: string, index: number, child: number): string {
  return `/api/cases/${encodeURIComponent(caseId)}/attachments/${index}/children/${child}`;
}

/** The feedback rows of a case the caller may see (`GET /cases/{id}/feedback`). */
export function useCaseFeedback(caseId: string, enabled = true) {
  return useQuery({
    queryKey: feedbackKey(caseId),
    queryFn: () => listCaseFeedback(caseId),
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

/** Record "right / wrong" on the customer or a line (`POST /cases/{id}/feedback`). */
export function useRecordFeedback(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CaseFeedbackRequest) => postCaseFeedback(caseId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKey(caseId) });
      void queryClient.invalidateQueries({ queryKey: ["case-events", caseId] });
      // The verdict is a feedback hub item too: "My feedback" lists it.
      void queryClient.invalidateQueries({ queryKey: feedbackHubKeys.all });
    },
  });
}

/** The cached reply draft of a case (`GET /cases/{id}/answer-draft`). */
export function useAnswerDraft(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["case-answer-draft", caseId],
    queryFn: () => getAnswerDraft(caseId),
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

/**
 * The mutation key of "Prepare reply" for one case. The decision strip and the reply panel
 * each have their own button, and both read the state of every request under this key, so
 * the panel shows "Preparing" and a failure whichever button asked (review F07).
 */
export function answerDraftCreateKey(caseId: string) {
  return ["case-answer-draft-create", caseId] as const;
}

/** Ask for a reply draft (`POST /cases/{id}/answer-draft`); the answer replaces the cached one. */
export function useCreateAnswerDraft(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: answerDraftCreateKey(caseId),
    mutationFn: (refresh: boolean) => createAnswerDraft(caseId, refresh),
    onSuccess: (draft) => {
      queryClient.setQueryData(["case-answer-draft", caseId], draft);
    },
  });
}

/** The query key of a case mail translation into one language. */
export function translationKey(caseId: string, target: TranslationTarget) {
  return ["case-translation", caseId, target] as const;
}

/**
 * The case mail in the viewer's language: `GET /cases/{id}/translation` first (cache or
 * same language, no model call), `POST` only when the GET says a translation is needed
 * (`loadCaseTranslation`). Asked once per case and language
 * and kept for the visit; never retried, so a failure falls back to the original at once
 * and is not asked again just by reopening the case.
 */
export function useCaseTranslation(caseId: string, target: TranslationTarget, enabled = true) {
  return useQuery({
    queryKey: translationKey(caseId, target),
    queryFn: () => loadCaseTranslation(caseId, target),
    enabled: enabled && caseId.trim().length > 0,
    staleTime: Infinity,
    retry: false,
    // A failure (busy service, hourly limit) is not asked again just by reopening the case.
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/** The approval for the order system and its history (`GET /cases/{id}/quotation-approval`). */
export function useQuotationApproval(caseId: string, enabled = true) {
  return useQuery({
    queryKey: approvalKey(caseId),
    queryFn: () => getQuotationApproval(caseId),
    enabled: enabled && caseId.trim().length > 0,
    retry: false,
  });
}

function useApprovalMutation<Args, Result>(
  caseId: string,
  send: (args: Args, version: number) => Promise<Result | undefined>,
) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  return useMutation(
    approvalMutationOptions<Args, Result>({
      queryClient,
      caseId,
      send,
      notify: (message) =>
        toast.add({ title: "Case changed", description: message, type: "warning" }),
    }),
  );
}

/** Approve the prepared quotation (`POST /cases/{id}/quotation-approval`). Nothing is sent. */
export function useApproveQuotation(caseId: string) {
  return useApprovalMutation<void, unknown>(caseId, (_args, version) =>
    postQuotationApproval(caseId, { expected_version: version }),
  );
}

/** Withdraw the approval (`POST /cases/{id}/quotation-approval/withdraw`). */
export function useWithdrawQuotation(caseId: string) {
  return useApprovalMutation<{ note?: string }, unknown>(caseId, (args, version) =>
    postQuotationWithdraw(caseId, { expected_version: version, note: args.note }),
  );
}

/** The cached case with the approval answer of one line applied to that line. */
export function applyPriceApproval<T extends CachedCase>(
  detail: T | undefined,
  lineIndex: number,
  answer: LinePriceApproval | undefined,
): T | undefined {
  if (!detail?.lines || !answer || !detail.lines[lineIndex]) {
    return detail;
  }
  return {
    ...detail,
    lines: detail.lines.map((line, index) =>
      index === lineIndex
        ? {
            ...line,
            price_approval: answer.approved ? "approved" : null,
            price_approved_by: answer.approved ? (answer.approved_by ?? null) : null,
            price_approved_at: answer.approved ? (answer.approved_at ?? null) : null,
          }
        : line,
    ),
  };
}

/**
 * Approve, or take back, the price of one line (`POST /cases/{id}/lines/{i}/price-approval`).
 * The line carries the approval at once; the next read of the case brings the rule list
 * that follows it (the hold is gone, or back).
 */
export function useLinePriceApproval(caseId: string) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const options = approvalMutationOptions<
    { lineIndex: number; approve: boolean },
    LinePriceApproval
  >({
    queryClient,
    caseId,
    send: ({ lineIndex, approve }, version) =>
      postLinePriceApproval(caseId, lineIndex, { expected_version: version, approve }),
    notify: (message) =>
      toast.add({ title: "Case changed", description: message, type: "warning" }),
  });
  return useMutation({
    ...options,
    onSuccess: (
      result: LinePriceApproval | undefined,
      args: { lineIndex: number; approve: boolean },
    ) => {
      queryClient.setQueryData<CachedCase>(["case", caseId], (detail) =>
        applyPriceApproval(detail, args.lineIndex, result),
      );
      options.onSuccess(result);
    },
  });
}

/** The pilot summary of one area (`GET /metrics/pilot`). Managers only. */
export function usePilotMetrics(window: PilotWindow, enabled: boolean) {
  return useQuery({
    queryKey: ["metrics-pilot", window.area, window.from ?? "", window.to ?? ""],
    queryFn: () => getPilotMetrics(window),
    enabled: enabled && window.area.trim().length > 0,
    retry: false,
  });
}

/** Customer matching right and wrong per area (`GET /metrics/customer-matching`). Managers only. */
export function useCustomerMatchingMetrics(
  window: number | { from: string; to: string },
  enabled: boolean,
  area?: string | null,
) {
  const days = typeof window === "number" ? matchingDays(window) : null;
  const dates = typeof window === "number" ? null : window;
  return useQuery({
    queryKey: dates
      ? ["metrics-customer-matching", "dates", dates.from, dates.to, area ?? ""]
      : ["metrics-customer-matching", days, area ?? ""],
    queryFn: () => getCustomerMatchingMetrics({ area, days: days ?? 30, ...(dates ?? {}) }),
    enabled,
    retry: false,
  });
}

/** The margin threshold per area (`GET /settings/margin-thresholds`). Managers who see the margin. */
export function useMarginThresholds(enabled: boolean) {
  return useQuery({
    queryKey: ["settings-margin-thresholds"],
    queryFn: getMarginThresholds,
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * The active entity rules of the caller's areas (`GET /rules`, capability `see_rules`:
 * managers and admins). Read only: rules are changed by IT in code and data files.
 */
export function useRuleSet(enabled: boolean) {
  return useQuery({
    queryKey: ["rules"],
    queryFn: async () => {
      const { data } = await getRuleSet();
      logger.debug("[rules] rule set loaded", {
        version: data?.version,
        entities: (data?.entities ?? []).length,
      });
      return data;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Paging and search of one rules data table. */
export interface RuleTableQuery {
  q?: string;
  offset?: number;
  limit?: number;
}

/**
 * One data table of an entity's rules, paged (`GET /rules/{entity}/tables/{table}`).
 * The search text is debounced; the previous page stays on screen while the next loads.
 */
export function useEntityRuleTable(
  entity: string,
  table: string,
  query: RuleTableQuery,
  enabled: boolean,
) {
  const q = useDebouncedValue((query.q ?? "").trim(), 300);
  const offset = Math.max(0, query.offset ?? 0);
  const limit = Math.min(200, Math.max(1, query.limit ?? 50));
  return useQuery({
    queryKey: ["rules-table", entity, table, q, offset, limit],
    queryFn: async () => {
      logger.debug("[rules] table", { entity, table, q, offset, limit });
      const { data } = await getEntityRuleTable({
        path: { entity, table },
        query: { offset, limit, ...(q ? { q } : {}) },
      });
      return data;
    },
    enabled: enabled && entity !== "" && table !== "",
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
}

/** Search the customers of the case's legal entity (`GET /cases/{id}/customers`). Same rules. */
export function useCustomerSearch(caseId: string, text: string) {
  const term = useDebouncedValue(text.trim(), 300);
  const query = useQuery({
    queryKey: ["case-customers", caseId, term],
    queryFn: async () => {
      const { data } = await client.get<{ 200: { items: CustomerHit[] } }, unknown, true>({
        url: "/cases/{case_id}/customers",
        path: { case_id: caseId },
        query: { q: term, limit: SEARCH_LIMIT },
        throwOnError: true,
      });
      return Array.isArray(data?.items) ? data.items : [];
    },
    enabled: caseId.trim().length > 0 && term.length >= SEARCH_MIN_CHARS,
    retry: false,
  });
  return { ...query, term };
}

// ─── Feedback hub (docs/feedback.md) ─────────────────────────────────────────
//
// Every write reaches the app DB only: no mail, no Teams message, no ticket. The badge
// is polled every two minutes and on window focus; there is no push.

const UNREAD_POLL_MS = 2 * 60_000;

/** Give feedback (`POST /feedback`). Refreshes "My feedback" and the case's quick verdicts. */
export function useCreateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FeedbackCreateRequest) => createFeedback(request),
    onSuccess: (_item, request) => {
      void queryClient.invalidateQueries({ queryKey: feedbackHubKeys.all });
      if (request.case_id) {
        void queryClient.invalidateQueries({ queryKey: feedbackKey(request.case_id) });
        void queryClient.invalidateQueries({ queryKey: ["case-events", request.case_id] });
      }
    },
  });
}

/** The signed-in person's own items (`GET /feedback/mine`). */
export function useMyFeedback(filters: MyFeedbackFilters = {}) {
  return useQuery({
    queryKey: feedbackHubKeys.mine(filters),
    queryFn: () => listMyFeedback(filters),
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** The badge on "My feedback" (`GET /feedback/unread-count`). */
export function useFeedbackUnreadCount(enabled = true) {
  return useQuery({
    queryKey: feedbackHubKeys.unread(),
    queryFn: getFeedbackUnreadCount,
    enabled,
    retry: false,
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** One item (`GET /feedback/{id}`): the author's view, or the triager's with internal fields. */
export function useFeedback(feedbackId: number | null) {
  return useQuery({
    queryKey: feedbackHubKeys.detail(feedbackId ?? 0),
    queryFn: () => getFeedback(feedbackId ?? 0),
    enabled: typeof feedbackId === "number" && feedbackId > 0,
    retry: false,
  });
}

/** Mark an own item as seen (`POST /feedback/{id}/seen`); clears it from the badge. */
export function useMarkFeedbackSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (feedbackId: number) => markFeedbackSeen(feedbackId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackHubKeys.unread() });
      void queryClient.invalidateQueries({ queryKey: ["feedback-hub", "mine"] });
    },
  });
}

/** Reply on an item, or (triagers) add an internal note (`POST /feedback/{id}/comments`). */
export function useAddFeedbackComment(feedbackId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FeedbackCommentRequest) => addFeedbackComment(feedbackId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackHubKeys.detail(feedbackId) });
      void queryClient.invalidateQueries({ queryKey: ["feedback-hub", "triage"] });
    },
  });
}

/** The triage list (`GET /feedback`). Managers and admins only. */
export function useFeedbackTriage(filters: FeedbackFilters, enabled = true) {
  return useQuery({
    queryKey: feedbackHubKeys.triage(filters),
    queryFn: () => listFeedback(filters),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** The counts above the triage list (`GET /feedback/summary`). */
export function useFeedbackSummary(filters: FeedbackFilters, enabled = true) {
  return useQuery({
    queryKey: feedbackHubKeys.summary(filters),
    queryFn: () => getFeedbackSummary(filters),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/**
 * Change status, owner, type, area, impact or eval label, or reply (`PATCH /feedback/{id}`).
 * A 409 (someone else changed it) reloads the item so the triager sees the new version.
 */
export function useTriageFeedback(feedbackId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FeedbackTriageRequest) => triageFeedback(feedbackId, request),
    onSuccess: (detail) => {
      if (detail && typeof detail === "object") {
        queryClient.setQueryData(feedbackHubKeys.detail(feedbackId), detail);
      }
      void queryClient.invalidateQueries({ queryKey: ["feedback-hub", "triage"] });
      void queryClient.invalidateQueries({ queryKey: ["feedback-hub", "summary"] });
    },
    onError: (error) => {
      if ((error as unknown as ApiError | null)?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: feedbackHubKeys.detail(feedbackId) });
      }
    },
  });
}

// ─── Discussions: open questions and decisions of record ─────────────────────
//
// Every write reaches the app DB only. A comment is shown under the author's name;
// the status and the decision text are a manager's (capability decide_discussion).

/**
 * The register (`GET /discussions`): open first, then decided, then parked. Without a
 * kind it holds the topics and the business decisions.
 */
export function useDiscussions(kind?: DiscussionKind) {
  return useQuery({
    queryKey: discussionKeys.list(kind),
    queryFn: () => listDiscussionsRaw(kind),
    retry: false,
  });
}

/** The business decisions only (`GET /discussions?kind=business_decision`, 15 Oct 2026). */
export function useBusinessDecisions() {
  return useDiscussions("business_decision");
}

/** One topic with its thread (`GET /discussions/{id}`). */
export function useDiscussion(discussionId: number | null) {
  return useQuery({
    queryKey: discussionKeys.detail(discussionId ?? 0),
    queryFn: () => getDiscussionRaw(discussionId ?? 0),
    enabled: discussionId !== null && discussionId > 0,
    retry: false,
  });
}

/** Open a topic (`POST /discussions`). */
export function useCreateDiscussion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DiscussionCreateRequest) => createDiscussionRaw(request),
    onSuccess: (detail) => {
      logger.debug("[discussions] opened", detail.discussion_id);
      queryClient.setQueryData(discussionKeys.detail(detail.discussion_id), detail);
      void queryClient.invalidateQueries({ queryKey: discussionKeys.all });
    },
  });
}

/** Comment on a topic (`POST /discussions/{id}/comments`); the answer is the whole topic. */
export function useAddDiscussionComment(discussionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => addDiscussionCommentRaw(discussionId, text),
    onSuccess: (detail) => {
      queryClient.setQueryData(discussionKeys.detail(discussionId), detail);
      void queryClient.invalidateQueries({ queryKey: discussionKeys.list() });
    },
  });
}

/** Set the status and the decision of record (`PATCH /discussions/{id}`), managers only. */
export function useDecideDiscussion(discussionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DiscussionDecisionRequest) => decideDiscussionRaw(discussionId, request),
    onSuccess: (detail) => {
      logger.debug("[discussions] decided", { id: discussionId, status: detail.status });
      queryClient.setQueryData(discussionKeys.detail(discussionId), detail);
      void queryClient.invalidateQueries({ queryKey: discussionKeys.list() });
    },
  });
}

/**
 * Set the owner role, the milestone, the options and what I2Q does today of a business
 * decision (`PUT /discussions/{id}/frame`), managers only. A topic becomes a business
 * decision with its first frame.
 */
export function useSetDecisionFrame(discussionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (frame: DecisionFrame) => setDecisionFrameRaw(discussionId, frame),
    onSuccess: (detail) => {
      logger.debug("[discussions] frame", { id: discussionId });
      queryClient.setQueryData(discussionKeys.detail(discussionId), detail);
      void queryClient.invalidateQueries({ queryKey: discussionKeys.list() });
    },
  });
}

// ─── Sales experience: fewer clicks, reminders inside the app, measurement ──────
//
// docs/ux-and-autonomy.md. Every write reaches the app DB only. Nothing is
// sent to a customer and nothing is written to F&O. The activity events feed the effort
// measurement, which is read only per area and week, never per person.

const ATTENTION_KEY = ["me-attention"] as const;

/** Every query a work write makes stale beyond the case itself. */
function invalidateWork(queryClient: ReturnType<typeof useQueryClient>, caseId?: string) {
  if (caseId) {
    void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
    void queryClient.invalidateQueries({ queryKey: ["case-events", caseId] });
    void queryClient.invalidateQueries({ queryKey: ["case-fo-draft", caseId] });
  }
  void queryClient.invalidateQueries({ queryKey: ["queue"] });
  void queryClient.invalidateQueries({ queryKey: ATTENTION_KEY });
}

/**
 * What {@link useAcceptMatched} takes: the line keys (null = every matched line), or the
 * keys with `includeSuggested` for "Confirm n items" (the suggested lines too).
 */
export type AcceptMatchedArgs =
  | string[]
  | null
  | { lineKeys: string[] | null; includeSuggested?: boolean };

function acceptArgs(input: AcceptMatchedArgs): {
  lineKeys: string[] | null;
  includeSuggested: boolean;
} {
  if (input === null || Array.isArray(input)) {
    return { lineKeys: input, includeSuggested: false };
  }
  return { lineKeys: input.lineKeys, includeSuggested: input.includeSuggested === true };
}

/**
 * "Accept matched items" (`POST /cases/{id}/lines/accept-matched`): Confirm on every line
 * I2Q matched and nobody decided yet, in one write. With `includeSuggested` it is "Confirm
 * n items": the suggested lines a person confirms with one click, shown at once. Undo
 * stays per line. A 409 raises the same conflict toast as every case write and reads the
 * case again.
 */
export function useAcceptMatched(caseId: string) {
  const queryClient = useQueryClient();
  const toast = useToastManager();
  const caseKey = ["case", caseId];
  return useMutation({
    // One after the other with every other write of the case (see `workMutationOptions`).
    mutationKey: caseWriteKey(caseId),
    scope: { id: caseWriteKey(caseId).join(":") },
    mutationFn: async (input: AcceptMatchedArgs = null) => {
      const { lineKeys, includeSuggested } = acceptArgs(input);
      const shown = queryClient.getQueryData<LiveQuoteCaseDetail>(caseKey);
      return postAcceptMatched(caseId, expectedVersion(shown?.work), lineKeys, includeSuggested);
    },
    onMutate: async (input: AcceptMatchedArgs = null): Promise<WorkMutationContext> => {
      const { lineKeys, includeSuggested } = acceptArgs(input);
      // "Confirm n items" names its lines: their rows move to "Matched" at once.
      if (!includeSuggested || !lineKeys) {
        return {};
      }
      await queryClient.cancelQueries({ queryKey: caseKey });
      const previous = queryClient.getQueryData<CachedCase>(caseKey);
      if (previous?.lines) {
        const keys = new Set(lineKeys);
        let next = previous;
        previous.lines.forEach((line, index) => {
          if (line.line_key && keys.has(line.line_key) && !line.decision) {
            next = optimisticDecision(next, index, "confirm");
          }
        });
        queryClient.setQueryData<CachedCase>(caseKey, next);
      }
      return { previous };
    },
    onSuccess: (result: AcceptMatchedResult | undefined) => {
      queryClient.setQueryData<LiveQuoteCaseDetail>(caseKey, (detail) =>
        applyAcceptResult(detail, result),
      );
      invalidateWork(queryClient, caseId);
    },
    onError: (error: unknown, _input?: AcceptMatchedArgs, context?: WorkMutationContext) => {
      const previous = context?.previous;
      if (previous) {
        queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
          detail ? { ...detail, lines: previous.lines } : previous,
        );
      }
      const failure = workFailure(error, "line");
      if (failure.kind === "conflict") {
        toast.add({ title: "Case changed", description: failure.message, type: "warning" });
      }
      if (failure.kind === "conflict" || previous) {
        void queryClient.invalidateQueries({ queryKey: caseKey });
      }
    },
  });
}

/** Arguments of {@link useBulkWork}: the rows as the queue showed them, and the change. */
export interface BulkWorkArgs {
  rows: BulkRow[];
  request: BulkRequest;
}

/**
 * One status, or Take, for many queue rows (`POST /work/bulk/status`, `/work/bulk/assign`).
 * Each case is its own write with the rules of the case page; the answer lists every case.
 */
export function useBulkWork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rows, request }: BulkWorkArgs): Promise<BulkResult> =>
      runBulk(rows, bulkSender(request), readCaseWork, (caseId) => {
        const cached = queryClient.getQueryData<LiveQuoteCaseDetail>(["case", caseId]);
        return cached?.work ? expectedVersion(cached.work) : null;
      }),
    onSuccess: (result: BulkResult) => {
      for (const entry of result.results ?? []) {
        if (entry.code === "ok") {
          void queryClient.invalidateQueries({ queryKey: ["case", entry.case_id] });
        }
      }
      invalidateWork(queryClient);
    },
  });
}

/** Arguments of {@link useConfirmSuggestedCustomer}. */
export interface ConfirmSuggestedArgs {
  row: BulkRow;
  account: string;
}

/**
 * "Confirm <name, city>" on a queue row: the same write as on the case page
 * (`POST /cases/{id}/customer`), one row at a time. There is no bulk confirm.
 */
export function useConfirmSuggestedCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ row, account }: ConfirmSuggestedArgs) =>
      sendWithRowVersion(
        row,
        (version) => postCaseCustomer(row.id, { account, expected_version: version }),
        // Only while nobody confirmed a customer in the meantime.
        (work) => !work?.customer_account,
      ),
    onSuccess: (_result, { row }) => {
      void queryClient.invalidateQueries({ queryKey: customerMemoryKey(row.id) });
      invalidateWork(queryClient, row.id);
    },
  });
}

/**
 * Record what the effort measurement counts (`POST /cases/{id}/activity`). Returns a
 * stable function; a failure is logged and never shown.
 */
export function useCaseActivity() {
  return useCallback(
    (caseId: string, body: CaseActivityRequest, leaving = false) =>
      sendCaseActivity(caseId, body, leaving),
    [],
  );
}

/** The "Back for you" badge (`GET /me/attention`), read at most once a minute. */
export function useMyAttention(enabled = true) {
  return useQuery({
    queryKey: ATTENTION_KEY,
    queryFn: async () => {
      const { data } = await getMyAttention({ throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** "What I2Q did" and the backlog for managers (`GET /metrics/autonomy`). */
/**
 * Open inquiries per area and owner (`GET /api/metrics/workload`, managers only): the
 * assignee, else the customer's usual owner (a suggestion), else no owner. Read only.
 */
export function useWorkloadMetrics(enabled: boolean) {
  return useQuery({
    queryKey: ["metrics-workload"],
    queryFn: async () => {
      const { data } = await getWorkloadMetrics({ throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useAutonomyMetrics(weeks: number, enabled: boolean) {
  const window = Math.min(26, Math.max(1, Math.round(weeks)));
  return useQuery({
    queryKey: ["metrics-autonomy", window],
    queryFn: async () => {
      const { data } = await getAutonomyMetrics({ query: { weeks: window }, throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}
